import { default as FetchService } from './FetchService';

export enum DexfConfigApplications {
	vallentuna = 'vallentuna',
	holmsund = 'holmsund',
	harlanda = 'harlanda',
	soderhamn = 'soderhamn',
	gronlid = 'gronlid',
	vimle = 'vimle',
	ektorp = 'ektorp',
	lidhult = 'lidhult',
	norsborg = 'norsborg',
	finnala = 'finnala',
	kivik = 'kivik',
	eket = 'eket',
}

export enum DexfConfigEndpoints {
	qa = 'https://api.dexf.qa.ikeadt.com/config/v1',
	prod = 'https://api.dexf.ikea.com/config/v1',
}

export enum DexfConfigSegments {
	dev = 'development',
	test = 'test',
	verification = 'verification',
	prod = 'production',
}

export interface IDexfApplicationConfigPing {
	data: string;
}

export interface IDexfApplicationConfigQuery {
	items: [{ itemId: string; tags?: string[]; attributes?: unknown }];
}

export interface IDexfApplicationConfigSegment {
	configItems: [
		{
			itemId: string;
			stage?: string;
			tags?: [{ tag: string; stage?: string; publishDate?: string; deleteDate?: string }];
			attributes?: [
				{ key: string; value: string; stage?: string; publishDate?: string; deleteDate?: string }
			];
			publishDate?: string;
			deleteDate?: string;
		}
	];
}

export interface IDexfApplicationConfigAttributes {
	attributes: [{ type: string; key?: string[]; description?: string }];
}

/*
 * IKEA DEXF Config service.
 *
 * DEXF Application Configu API:
 * https://app.swaggerhub.com/apis/DEXF/config-dexf_api/3.2.0.
 *
 * Example
 *  import {
 *		DexfConfigService,
 *		DexfConfigEndpoints,
 *		DexfConfigSegments,
 *	} from '../../services';
 *
 *	const dexfConfigService = new DexfConfigService({
 *		dexfApplicationId: settings.applicationName,
 *		dexfApiKey: localization?.dexfApiKey || '',
 *		dexfSecurityToken: '<dexf-security-token>',
 *		endpoint: DexfConfigEndpoints.qa,
 *	});
 *	console.log(
 *		'DEXF application config',
 *		dexfConfigService.getConfiguration({
 *			segment: DexfConfigSegments.dev,
 *			filterTags: 'tag',
 *		})
 *	);
 */
export default class DexfConfigService {
	private dexfApplicationId: DexfConfigApplications;
	private dexfApiKey: string;
	private dexfSecurityToken: string;
	private endpoint: DexfConfigEndpoints;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.dexfApplicationId DEXF application ID.
	 * @param options.endpoint Endpoint.
	 * @param options.dexfApiKey DEXF API key.
	 * @param options.dexfSecurityToken DEXF Id token needed for further access of APIs.
	 */
	constructor(options: {
		dexfApplicationId: DexfConfigApplications;
		dexfApiKey: string;
		endpoint: DexfConfigEndpoints;
		dexfSecurityToken?: string;
	}) {
		this.dexfApplicationId = options.dexfApplicationId;
		this.endpoint = options.endpoint;
		this.dexfApiKey = options.dexfApiKey;
		this.dexfSecurityToken = options.dexfSecurityToken || '';
	}

	/**
	 * This operation is used to ping the config service.
	 * It returns pong and is used for test to see that the service is responding correctly.
	 *
	 * @param future Should future queries be used?
	 * @returns True if it got pong.
	 */
	public async ping(future?: boolean): Promise<boolean> {
		const { data } = await this.pingData(future);
		return data === 'pong';
	}

	/**
	 * This operation is used to ping the admin service.
	 * It returns pong and is used for test to see that the service is responding correctly.
	 *
	 * @returns True if it got pong.
	 */
	public async pingAdmin(): Promise<boolean> {
		const { data } = await this.pingAdminData();
		return data === 'pong';
	}

	/**
	 * This operation returns all the items that are available for selected application in a specific segment
	 * with configuration per item, tags, meta data etc.
	 *
	 * @param options Options.
	 * @param options.segment The segment for which information is wanted.
	 * @param options.filterTags The tags wanted.
	 * @param options.future Hould future queries be used?
	 * @returns App configuration for the specific application with detailed information.
	 */
	public async getConfiguration(options: {
		segment?: DexfConfigSegments;
		filterTags?: string;
		future?: boolean;
	}): Promise<unknown> {
		const data = await this.getConfigurationData(options);
		if (data && data['items'] && Array.isArray(data['items'])) {
			return [...data['items']];
		}
		return null;
	}

	/**
	 * This operation returns all the attribute keys that are available for selected application.
	 *
	 * @returns Application config attributes.
	 */
	public async getAttributes(): Promise<unknown> {
		const data = await this.getAttributeAdminData();
		if (data && data['attributes'] && Array.isArray(data['attributes'])) {
			return [...data['attributes']];
		}
		return null;
	}

	/**
	 * This operation returns all the articles that are available for selected application in a specific segment.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 */
	public async getSegment(options: { segment: DexfConfigSegments }): Promise<unknown> {
		const data = await this.getSegmentAdminData(options);
		if (data && data['configItems'] && Array.isArray(data['configItems'])) {
			return [...data['configItems']];
		}
		return null;
	}

	/**
	 * This operation is setting the stage to add articles in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemIds Article numbers that is to be added to the application.
	 */
	public async addSegmentItems(options: {
		segment: DexfConfigSegments;
		itemIds: string[];
	}): Promise<void> {
		let delay = 0;
		await Promise.all(
			options.itemIds.map(
				(itemId) =>
					new Promise((resolve, reject) => {
						setTimeout(
							() =>
								this.addSegmentItemAdminData({ segment: options.segment, itemId })
									.then(resolve)
									.catch(reject),
							delay
						);
						delay += 50;
					})
			)
		);
	}

	/**
	 * This operation is setting the stage to add a specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be added to the application.
	 */
	public async addSegmentItem(options: {
		segment: DexfConfigSegments;
		itemId: string;
	}): Promise<void> {
		await this.addSegmentItemAdminData(options);
	}

	/**
	 * This operation is setting the stage to remove articles in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemIds Article numbers that is to be added to the application.
	 */
	public async removeSegmentItems(options: {
		segment: DexfConfigSegments;
		itemIds: string[];
	}): Promise<void> {
		let delay = 0;
		await Promise.all(
			options.itemIds.map(
				(itemId) =>
					new Promise((resolve, reject) => {
						setTimeout(
							() =>
								this.removeSegmentItemAdminData({ segment: options.segment, itemId })
									.then(resolve)
									.catch(reject),
							delay
						);
						delay += 50;
					})
			)
		);
	}

	/**
	 * This operation is setting the stage to delete on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be added to the application.
	 */
	public async removeSegmentItem(options: {
		segment: DexfConfigSegments;
		itemId: string;
	}): Promise<void> {
		await this.removeSegmentItemAdminData(options);
	}

	/**
	 * This operation is setting the stage to add tags on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be updated.
	 * @param options.tags Tags to be added to article.
	 */
	public async addSegmentItemTags(options: {
		segment: DexfConfigSegments;
		itemId: string;
		tags: string[];
	}): Promise<void> {
		let delay = 0;
		await Promise.all(
			options.tags.map(
				(tag) =>
					new Promise((resolve, reject) => {
						setTimeout(
							() =>
								this.addSegmentItemTagAdminData({
									segment: options.segment,
									itemId: options.itemId,
									tag,
								})
									.then(resolve)
									.catch(reject),
							delay
						);
						delay += 50;
					})
			)
		);
	}

	/**
	 * This operation is setting the stage to add a tag on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be updated.
	 * @param options.tag Tag to be added to article.
	 */
	public async addSegmentItemTag(options: {
		segment: DexfConfigSegments;
		itemId: string;
		tag: string;
	}): Promise<void> {
		await this.addSegmentItemTagAdminData(options);
	}

	/**
	 * This operation is setting the stage to remove tags on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be updated.
	 * @param options.tags Tags to be added to article.
	 */
	public async removeSegmentItemTags(options: {
		segment: DexfConfigSegments;
		itemId: string;
		tags: string[];
	}): Promise<void> {
		let delay = 0;
		await Promise.all(
			options.tags.map(
				(tag) =>
					new Promise((resolve, reject) => {
						setTimeout(
							() =>
								this.removeSegmentItemTagAdminData({
									segment: options.segment,
									itemId: options.itemId,
									tag,
								})
									.then(resolve)
									.catch(reject),
							delay
						);
						delay += 50;
					})
			)
		);
	}

	/**
	 * This operation is setting the stage to remove a tag on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be updated.
	 * @param options.tag Tag to be added to article.
	 */
	public async removeSegmentItemTag(options: {
		segment: DexfConfigSegments;
		itemId: string;
		tag: string;
	}): Promise<void> {
		await this.removeSegmentItemTagAdminData(options);
	}

	/**
	 * This operation is setting the stage to add attributes on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be updated.
	 * @param options.attributes The attributes.
	 */
	public async addSegmentItemAttributes(options: {
		segment: DexfConfigSegments;
		itemId: string;
		attributes: Record<string, string | boolean | number>;
	}): Promise<void> {
		let delay = 0;
		await Promise.all(
			Object.keys(options.attributes).map(
				(key) =>
					new Promise((resolve, reject) => {
						setTimeout(
							() =>
								this.addSegmentItemAttributeAdminData({
									segment: options.segment,
									itemId: options.itemId,
									attribute: { key, value: options.attributes[key] },
								})
									.then(resolve)
									.catch(reject),
							delay
						);
						delay += 50;
					})
			)
		);
	}

	/**
	 * This operation is setting the stage to add an attribute on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be updated.
	 * @param options.attribute The attribute.
	 * @param options.attribute.key The key of the attribute.
	 * @param options.attribute.value The value of the attribute.
	 */
	public async addSegmentItemAttribute(options: {
		segment: DexfConfigSegments;
		itemId: string;
		attribute: { key: string; value: string | boolean | number };
	}): Promise<void> {
		await this.addSegmentItemAttributeAdminData(options);
	}

	/**
	 * This operation is setting the stage to add a specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 */
	public async publishSegment(options: { segment: DexfConfigSegments }): Promise<void> {
		await this.publishSegmentAdminData(options);
	}

	/**
	 * This operation is used to ping the config service.
	 * It returns pong and is used for test to see that the service is responding correctly.
	 *
	 * @param future Should future queries be used?
	 * @returns Pong, used for testing.
	 */
	private async pingData(future?: boolean): Promise<IDexfApplicationConfigPing> {
		const url = `${this.endpoint}/query${future ? '-future' : ''}/ping`;
		return (await FetchService.json(url, {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
			},
		})) as IDexfApplicationConfigPing;
	}

	/**
	 * This operation returns all the items that are available for selected application in a specific segment
	 * with configuration per item, tags, meta data etc.
	 *
	 * @param options Options.
	 * @param options.segment The segment for which information is wanted.
	 * @param options.filterTags The tags wanted.
	 * @param options.future Hould future queries be used?
	 * @returns App configuration for the specific application with detailed information.
	 */
	private async getConfigurationData(options: {
		segment?: DexfConfigSegments;
		filterTags?: string;
		future?: boolean;
	}): Promise<IDexfApplicationConfigQuery> {
		const endpoint = `${this.endpoint}/query${options.future ? '-future' : ''}/apps/${
			this.dexfApplicationId
		}`;
		const fullendpoint = options.segment
			? `${endpoint}/segments/${options.segment}/detailed`
			: `${endpoint}/detailed`;
		const url = options.filterTags
			? `${fullendpoint}?filter.tags=${options.filterTags}`
			: fullendpoint;
		return (await FetchService.json(url, {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
			},
		})) as IDexfApplicationConfigQuery;
	}

	/**
	 * This operation publishes the stage configuration to be the current configuration.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 */
	private async getSegmentAdminData(options: {
		segment: DexfConfigSegments;
	}): Promise<IDexfApplicationConfigSegment> {
		const url = `${this.getSegmentEndpoint(options.segment)}`;
		return (await FetchService.json(url, {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
				authorization: 'Bearer ' + this.dexfSecurityToken,
			},
		})) as IDexfApplicationConfigSegment;
	}

	/**
	 * This operation returns all the attribute keys that are available for selected application.
	 *
	 * @returns Application config attributes.
	 */
	private async getAttributeAdminData(): Promise<IDexfApplicationConfigAttributes> {
		const url = `${this.endpoint}/admin/apps/${this.dexfApplicationId}/attributes`;
		return (await FetchService.json(url, {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
				authorization: 'Bearer ' + this.dexfSecurityToken,
			},
		})) as IDexfApplicationConfigAttributes;
	}

	/**
	 * This operation is used to ping the admin service.
	 * It returns pong and is used for test to see that the service is responding correctly.
	 *
	 * @returns Pong, used for testing.
	 */
	private async pingAdminData(): Promise<IDexfApplicationConfigPing> {
		const url = `${this.endpoint}/admin/ping`;
		return (await FetchService.json(url, {
			method: 'GET',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
				authorization: 'Bearer ' + this.dexfSecurityToken,
			},
		})) as IDexfApplicationConfigPing;
	}

	/**
	 * This operation is setting the stage to add a specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be added to the application.
	 */
	private async addSegmentItemAdminData(options: {
		segment: DexfConfigSegments;
		itemId: string;
	}): Promise<void> {
		const url = `${this.getSegmentEndpoint(options.segment)}/items/${options.itemId}`;
		await FetchService.fetch(url, {
			method: 'PUT',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
				authorization: 'Bearer ' + this.dexfSecurityToken,
			},
		});
	}

	/**
	 * This operation is setting the stage to delete on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be added to the application.
	 */
	private async removeSegmentItemAdminData(options: {
		segment: DexfConfigSegments;
		itemId: string;
	}): Promise<void> {
		const url = `${this.getSegmentEndpoint(options.segment)}/items/${options.itemId}`;
		await FetchService.fetch(url, {
			method: 'DELETE',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
				authorization: 'Bearer ' + this.dexfSecurityToken,
			},
		});
	}

	/**
	 * This operation publishes the stage configuration to be the current configuration.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 */
	private async publishSegmentAdminData(options: { segment: DexfConfigSegments }): Promise<void> {
		const url = `${this.getSegmentEndpoint(options.segment)}/publish`;
		await FetchService.fetch(url, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
				authorization: 'Bearer ' + this.dexfSecurityToken,
			},
		});
	}

	/**
	 * This operation is setting the stage to add a tag on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be updated.
	 * @param options.tag Tag to be added to article.
	 */
	private async addSegmentItemTagAdminData(options: {
		segment: DexfConfigSegments;
		itemId: string;
		tag: string;
	}): Promise<void> {
		const url = `${this.getSegmentEndpoint(options.segment)}/items/${options.itemId}/tags/${
			options.tag
		}`;
		await FetchService.fetch(url, {
			method: 'PUT',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
				authorization: 'Bearer ' + this.dexfSecurityToken,
			},
		});
	}

	/**
	 * This operation is setting the stage to delete tag on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be updated.
	 * @param options.tag Tag to be added to article.
	 */
	private async removeSegmentItemTagAdminData(options: {
		segment: DexfConfigSegments;
		itemId: string;
		tag: string;
	}): Promise<void> {
		const url = `${this.getSegmentEndpoint(options.segment)}/items/${options.itemId}/tags/${
			options.tag
		}`;
		await FetchService.fetch(url, {
			method: 'DELETE',
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
				authorization: 'Bearer ' + this.dexfSecurityToken,
			},
		});
	}

	/**
	 * This operation is setting the stage to add an attribute on specified article in application and segment of choice.
	 *
	 * @param options Options.
	 * @param options.segment The segment that is being updated.
	 * @param options.itemId Article number that is to be updated.
	 * @param options.attribute The attribute.
	 * @param options.attribute.key The key of the attribute.
	 * @param options.attribute.value The value of the attribute.
	 */
	private async addSegmentItemAttributeAdminData(options: {
		segment: DexfConfigSegments;
		itemId: string;
		attribute: { key: string; value: string | boolean | number };
	}): Promise<void> {
		const url = `${this.getSegmentEndpoint(options.segment)}/items/${
			options.itemId
		}/attributes/keys/${options.attribute.key}`;
		await FetchService.fetch(url, {
			method: 'PUT',
			body: JSON.stringify({ value: options.attribute.value }),
			credentials: 'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey,
				authorization: 'Bearer ' + this.dexfSecurityToken,
			},
		});
	}

	/**
	 * Gets url endpoint for segment.
	 *
	 * @param segment The segment that is being updated.
	 * @returns Url endpoint to segment.
	 */
	private getSegmentEndpoint(segment: string): string {
		return `${this.endpoint}/admin/apps/${this.dexfApplicationId}/segments/${segment}`;
	}
}
